import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '../components/icons';
import { InvoicePill, useHasOpenInvoices } from '../features/billing/invoices';
import { PlanBadge } from '../features/billing/PlanBadge';
import { useBodyScrollLock, useFocusTrap } from '../lib/overlay';
import { useCommandPalette } from './CommandPalette';
import { MOBILE_TABS, MORE_ITEMS, isNavActive } from './nav';
import { AccountMenu, WorkspaceSwitcher, useUnreadCount } from './ShellControls';

/**
 * Phone chrome (refonte), shown below md (768px) where the sidebar hides: a plum top bar
 * with the workspace, search and the account avatar; a bottom tab bar for the
 * four core areas plus More; and the More sheet for everything else. Every
 * target is at least 44px tall.
 */

export function MobileTopBar() {
  const { t } = useTranslation();
  const palette = useCommandPalette();
  return (
    <header className="sticky top-0 z-40 flex items-center gap-2.5 bg-[image:var(--gradient-sidebar)] px-4 pb-2.5 pt-[max(env(safe-area-inset-top),10px)] md:hidden">
      <WorkspaceSwitcher compact />
      <button
        type="button"
        onClick={palette.open}
        aria-label={t('nav.search')}
        className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-white/7 text-white transition-colors hover:bg-white/12"
      >
        <Icon name="search" className="size-[18px]" />
      </button>
      <AccountMenu variant="avatar" />
    </header>
  );
}

/** Tailwind's `md`: where the sidebar takes over from this chrome. */
const SIDEBAR_FROM_PX = 768;

/** Widths follow the labels: "Automatisations" needs more room than "Plus" (Q5). */
const TAB =
  'relative flex h-[52px] min-w-11 flex-auto flex-col items-center justify-center gap-[3px] whitespace-nowrap px-0.5 text-[10.5px] font-semibold transition-colors';

export function MobileTabBar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const unread = useUnreadCount();
  const invoiceDue = useHasOpenInvoices();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreActive = MORE_ITEMS.some((entry) => isNavActive(entry, pathname));
  const closeMore = useCallback(() => setMoreOpen(false), []);

  // Any navigation closes the sheet — a link in it, but also Back — and so
  // does growing the window past md, where the sheet is hidden but would
  // keep its scroll lock.
  useEffect(() => setMoreOpen(false), [pathname]);
  useEffect(() => {
    if (!moreOpen || typeof window.matchMedia !== 'function') return;
    const desktop = window.matchMedia(`(min-width: ${SIDEBAR_FROM_PX}px)`);
    const onChange = () => {
      if (desktop.matches) setMoreOpen(false);
    };
    onChange();
    desktop.addEventListener('change', onChange);
    return () => desktop.removeEventListener('change', onChange);
  }, [moreOpen]);

  return (
    <>
      <nav
        aria-label={t('nav.main')}
        className="fixed inset-x-0 bottom-0 z-40 flex border-t border-(--color-line) bg-(--color-surface) px-2 pb-[max(env(safe-area-inset-bottom),6px)] pt-1.5 md:hidden"
      >
        {MOBILE_TABS.map((tab) => {
          const active = isNavActive(tab, pathname);
          const badge = tab.to === '/inbox' ? unread : 0;
          return (
            <Link
              key={tab.to}
              to={tab.to}
              aria-current={active ? 'page' : undefined}
              className={`${TAB} ${active ? 'text-(--color-accent)' : 'text-(--color-ink-700)'}`}
            >
              <Icon name={tab.icon} className="size-[21px]" />
              {t(`nav.${tab.label}`)}
              {badge > 0 ? (
                <span className="absolute left-[calc(50%+6px)] top-1 flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-(--color-highlight) px-[5px] text-[10px] font-bold text-white ring-2 ring-(--color-surface)">
                  {badge}
                  <span className="sr-only"> {t('nav.unread', { count: badge })}</span>
                </span>
              ) : null}
            </Link>
          );
        })}
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          // The dot has no words of its own: the tab's name says what it means (A00-08).
          aria-label={invoiceDue ? t('nav.moreWithInvoice') : undefined}
          onClick={() => setMoreOpen(true)}
          className={`${TAB} ${moreActive ? 'text-(--color-accent)' : 'text-(--color-ink-700)'}`}
        >
          <Icon name="more" className="size-[21px]" />
          {t('nav.more')}
          {/* No room for the pill in a tab: the dot leads to the sheet, where "Facture" says what it is. */}
          {invoiceDue ? (
            <span aria-hidden className="absolute left-[calc(50%+8px)] top-2 size-2 rounded-full bg-(--color-caution) ring-2 ring-(--color-surface)" />
          ) : null}
        </button>
      </nav>
      {moreOpen ? <MoreSheet onClose={closeMore} invoiceDue={invoiceDue} /> : null}
    </>
  );
}

function MoreSheet({ onClose, invoiceDue }: { onClose: () => void; invoiceDue: boolean }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const sheetRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock();
  // Focus starts on the first row and returns to More when the sheet closes.
  useFocusTrap(sheetRef);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end md:hidden" role="presentation" onClick={onClose}>
      <div className="absolute inset-0 animate-fade-in bg-[rgba(23,19,46,0.35)]" aria-hidden />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.more')}
        onClick={(e) => e.stopPropagation()}
        className="relative flex w-full animate-sheet-up flex-col gap-0.5 rounded-t-[22px] bg-(--color-surface) px-4 pb-[calc(env(safe-area-inset-bottom)+20px)] pt-2.5"
      >
        <div aria-hidden className="mx-auto mb-2.5 h-1 w-9 rounded-full bg-(--color-line-control)" />
        {MORE_ITEMS.map((entry) => {
          const active = isNavActive(entry, pathname);
          return (
            <Link
              key={entry.to}
              to={entry.to}
              onClick={onClose}
              aria-current={active ? 'page' : undefined}
              className={`flex h-12 items-center gap-3.5 rounded-xl px-2 text-[15px] font-medium transition-colors hover:bg-(--color-surface-sunken) ${
                active ? 'text-(--color-accent)' : 'text-(--color-ink-900)'
              }`}
            >
              <Icon name={entry.icon} className={`size-[19px] ${active ? '' : 'text-(--color-ink-700)'}`} />
              <span className="flex-1">{t(`nav.${entry.label}`)}</span>
              {entry.to === '/settings' && invoiceDue ? <InvoicePill /> : null}
              {entry.feature ? <PlanBadge feature={entry.feature} /> : null}
            </Link>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}
